/**
 * HTTP client.
 *
 * Two things matter here:
 *
 * 1. The access token lives in memory only, never in localStorage. The refresh
 *    token is an httpOnly cookie the browser sends automatically, so an XSS bug
 *    cannot exfiltrate a long-lived credential.
 * 2. A 401 triggers exactly one refresh attempt, and concurrent requests share
 *    that single attempt rather than each firing their own.
 */

import type { ApiErrorBody, SessionResponse } from '@/types/api';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000').replace(/\/$/, '');
const API_PREFIX = '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'unknown_error';
    this.details = body?.error?.details;
    this.requestId = body?.error?.request_id;
  }

  /** Field-level messages from a validation error, for form display. */
  get fieldErrors(): Record<string, string> {
    const fields = this.details?.fields;
    if (!Array.isArray(fields)) return {};
    const result: Record<string, string> = {};
    for (const entry of fields) {
      if (entry && typeof entry === 'object' && 'field' in entry && 'message' in entry) {
        result[String(entry.field)] = String(entry.message);
      }
    }
    return result;
  }
}

// --------------------------------------------------------------- auth state
let accessToken: string | null = null;
let activeOrganizationId: string | null = null;
let refreshPromise: Promise<boolean> | null = null;

type AuthListener = (authenticated: boolean) => void;
const listeners = new Set<AuthListener>();

export function onAuthChange(listener: AuthListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(authenticated: boolean): void {
  listeners.forEach((listener) => listener(authenticated));
}

/**
 * Resolve a usable Storage implementation.
 *
 * A bare `localStorage` reference is not always the browser one: server-side
 * rendering has none, sandboxed iframes throw on access, and some Node
 * runtimes define an unusable global of the same name that shadows the DOM's.
 * Returning null lets callers degrade instead of crashing.
 */
function safeStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
    if (typeof globalThis !== 'undefined' && globalThis.localStorage) return globalThis.localStorage;
  } catch {
    // Access itself throws when site data is blocked.
  }
  return null;
}

export function setSession(session: SessionResponse | null): void {
  accessToken = session?.access_token ?? null;
  if (session?.active_organization_id) {
    activeOrganizationId = session.active_organization_id;
  }
  if (!session) activeOrganizationId = null;
  notify(Boolean(accessToken));
}

export function setActiveOrganization(organizationId: string | null): void {
  activeOrganizationId = organizationId;
  // Remembering only the *selection* is safe; it is not a credential and the
  // server re-checks membership on every request.
  const storage = safeStorage();
  if (!storage) return;
  try {
    if (organizationId) storage.setItem('seo.activeOrg', organizationId);
    else storage.removeItem('seo.activeOrg');
  } catch {
    // Private browsing or blocked storage: selection simply is not remembered.
  }
}

export function getActiveOrganization(): string | null {
  if (activeOrganizationId) return activeOrganizationId;
  try {
    return safeStorage()?.getItem('seo.activeOrg') ?? null;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return Boolean(accessToken);
}

// ---------------------------------------------------------------- refreshing
async function refreshSession(): Promise<boolean> {
  // Collapse concurrent refreshes into one in-flight request.
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}${API_PREFIX}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!response.ok) {
        setSession(null);
        return false;
      }
      const session = (await response.json()) as SessionResponse;
      setSession(session);
      return true;
    } catch {
      setSession(null);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ------------------------------------------------------------------ requests
interface RequestOptions {
  method?: string;
  responseType?: 'json' | 'blob';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Internal: prevents an infinite refresh loop. */
  retryOnUnauthorized?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${BASE_URL}${API_PREFIX}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, retryOnUnauthorized = true } = options;

  const headers: Record<string, string> = { Accept: options.responseType === 'blob' ? 'application/pdf' : 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const organizationId = getActiveOrganization();
  if (organizationId) headers['X-Organization-Id'] = organizationId;

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (response.status === 401 && retryOnUnauthorized) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return request<T>(path, { ...options, retryOnUnauthorized: false });
    }
  }

  if (!response.ok) {
    let parsed: ApiErrorBody | null = null;
    try {
      parsed = (await response.json()) as ApiErrorBody;
    } catch {
      parsed = null;
    }
    throw new ApiError(response.status, parsed, `Request failed with status ${response.status}`);
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  if (options.responseType === 'blob') return (await response.blob()) as T;
  return (await response.json()) as T;
}

export const api = {
  blob: (path: string, query?: RequestOptions['query']) => request<Blob>(path, { responseType: 'blob', query }),
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'POST', body, query }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** Restore a session on page load using the refresh cookie. */
export async function bootstrapSession(): Promise<boolean> {
  return refreshSession();
}
